import React, { useContext, useMemo, useState } from "react";
import * as Icons from "Components/Icons";
import { Button, Modal, ModalBody, ModalHeader, ModalFooter, ModalProps, SpinningButton, Row } from "Components/ReactstrapSlim";
import { CancellationPolicy, ITeamMemberReservation, ReservationMethod, ReservationsClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import exhaustiveCheck from "Utils/exhaustiveCheck";
import { LayoutContext } from "Components/LayoutContext";

interface CancelReservationModalProps extends ModalProps {
   reservation: ITeamMemberReservation,
   container?: string | HTMLElement;
   onClose: () => void,
   onSingleSuccess: () => void,
   onRecurrentSuccess: () => void
}

enum RecurrenceCancelOptions {
   AllEvents = 1,
   SingleEvent = 2,
   AllFollowing = 3
}

const CancelReservationModal: React.FC<CancelReservationModalProps> = ({ reservation, container, onSingleSuccess, onRecurrentSuccess, onClose, ...modalProps }) => {
   const [allIsLoading, setAllIsLoading] = useState(false);
   const [allFollowingIsLoading, setAllFollowingIsLoading] = useState(false);
   const [singleIsLoading, setSingleIsLoading] = useState(false);

   const reservationClient = useApiClient(ReservationsClient);

   const getConfirmationLink = () =>
      reservation.reservationMethod == ReservationMethod.Monthly
         ? "/Terms/Monthly-Space-License-Agreement"
         : "/Terms/Hourly-Space-License-Agreement";

   const getConfirmationText = () =>
      reservation.reservationMethod == ReservationMethod.Monthly
         ? "Flexible Cancellation Policy"
         : "Cancellation Policy";

   const layoutContext = useContext(LayoutContext);

   const deleteReservation = async () => {
      try {
         setSingleIsLoading(true);
         const result = await reservationClient.cancel(reservation.idForLink, undefined, undefined);
         if (result.success) {
            await onSingleSuccess();
            onClose();
         }
      }
      catch (exc) {
         layoutContext.showError(exc);
      }
      finally {
         setSingleIsLoading(false);
      }
   }

   const deleteRecurrentReservation = async (option: RecurrenceCancelOptions) => {
      try {
         let occurrenceDate: string | undefined = undefined;
         let applyDate: string | undefined = undefined;

         if (option == RecurrenceCancelOptions.SingleEvent) {
            setSingleIsLoading(true);
            occurrenceDate = reservation.occurrenceDate;
         } else if (option == RecurrenceCancelOptions.AllFollowing) {
            setAllFollowingIsLoading(true);
            applyDate = reservation.occurrenceDate;
         } else {
            setAllIsLoading(true);
         }

         const result = await reservationClient.cancel(reservation.idForLink, occurrenceDate, applyDate);
         if (result.success) {
            if (occurrenceDate) {
               await onSingleSuccess();
            } else {
               await onRecurrentSuccess();
            }
            onClose();
         }
      }
      catch (exc) {
         console.log(exc);
      }
      finally {
         setSingleIsLoading(false);
         setAllFollowingIsLoading(false);
         setAllIsLoading(false);
      }
   }

   const cancellationPolicyName = useMemo(() => {
      switch (reservation.cancellationPolicy) {
         case CancellationPolicy.FlexibleCancellation:
            return "Flexible 24 hour";
         case CancellationPolicy.NoCancellation:
            return "Strict";
         case CancellationPolicy.Moderate7:
            return "Moderate 7 day";
         case CancellationPolicy.Moderate14:
            return "Moderate 14 day";
         case CancellationPolicy.Extended:
            return "Extended 30 day";
         case CancellationPolicy.Regus:
            return "IWG";
         default:
            exhaustiveCheck(reservation.cancellationPolicy);
            return "";
      }
   }, [reservation.cancellationPolicy])

   return (<Modal className="cancel-reservation-modal" container={container} toggle={() => onClose()} isOpen={true} centered {...modalProps}>
      <ModalHeader toggle={() => onClose()}>Cancel Reservation</ModalHeader>
      <ModalBody>
         {
            reservation.occurrenceDate ? <div className="cancel-reservation-modal-recurrent">
               <div className="cancel-reservation-modal-text">Would you like to delete only this reservation or all reservations in the series?</div>
               <div className="cancel-reservation-modal-recurrent-actions">
                  <Row className="cancel-reservation-modal-recurrent-actions-row">
                     <div className="col-12 col-sm-6">
                        <SpinningButton size="md" color="primary" isLoading={allIsLoading} disabled={allFollowingIsLoading || singleIsLoading} onClick={async () => await deleteRecurrentReservation(RecurrenceCancelOptions.AllEvents)}>
                           All reservations
                        </SpinningButton>
                     </div>
                     <div className="col-12 col-sm-6">
                        <div>All reservations in the series will be deleted.</div>
                     </div>
                  </Row>
                  <Row className="cancel-reservation-modal-recurrent-actions-row">
                     <div className="col-12 col-sm-6">
                        <SpinningButton size="md" color="primary" isLoading={allFollowingIsLoading} disabled={allIsLoading || singleIsLoading} onClick={async () => await deleteRecurrentReservation(RecurrenceCancelOptions.AllFollowing)} >
                           All following
                        </SpinningButton>
                     </div>
                     <div className="col-12 col-sm-6">
                        <div>This and all the following reservations will be deleted.</div>
                     </div>
                  </Row>
                  <Row className="cancel-reservation-modal-recurrent-actions-row">
                     <div className="col-12 col-sm-6">
                        <SpinningButton size="md" color="primary" isLoading={singleIsLoading} disabled={allIsLoading || allFollowingIsLoading} onClick={async () => await deleteRecurrentReservation(RecurrenceCancelOptions.SingleEvent)} >
                           Only this reservation
                        </SpinningButton>
                     </div>
                     <div className="col-12 col-sm-6">
                        <div>All other reservations in the series will remain.</div>
                     </div>
                  </Row>
               </div>
            </div>
               :
               <div className="cancel-reservation-modal-text">
                  <div>Are you sure you want to cancel your reservation?</div>
                  <div>This reservation will be deleted immediately. Information about refunds is available on <a href={getConfirmationLink()} target="_blank">{getConfirmationText()}</a> page.</div>
               </div>

         }
      </ModalBody>
      <ModalFooter className="cancel-reservation-modal-footer">
         <div className="cancel-reservation-modal-cancelation-policy">
            <Icons.CancellationPolicy /><span>{cancellationPolicyName} cancellation policy</span>
         </div>
         <Button size="md" color="secondary" onClick={() => onClose()}>No</Button>
         {!reservation.occurrenceDate && <SpinningButton size="md" color="primary" isLoading={singleIsLoading} onClick={async () => await deleteReservation()} >
            Yes
         </SpinningButton>}
      </ModalFooter>
   </Modal>);
}

export default CancelReservationModal;